import React from "react";
import { Link } from "react-router-dom";

import styled, { css } from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Button from "components/atoms/button";
import Text from "components/atoms/text/Text";

import NavigatorFolderTreeView from "components/organisms/navigator/NavigatorFolderTreeView";
import NavigatorTagTreeView from "components/organisms/navigator/NavigatorTagTreeView";

import Constants from "utils/constants";
import { isFreePlanUser, isReadonlyUser } from "utils/user";

export const SIDE_WIDTH_MIN = 48;
export const SIDE_WIDTH_BASE = 280;
export const SIDE_WIDTH_MAX = 600;
export const SIDE_WIDTH_AUTOFOLD = 200;

const SIDE_WIDTH_MIN_PX = `${SIDE_WIDTH_MIN}px`;
const SIDE_WIDTH_BASE_PX = `${SIDE_WIDTH_BASE}px`;
const SIDE_WIDTH_MAX_PX = `${SIDE_WIDTH_MAX}px`;
const SIDEBAR_TAB = ["Folders", "Tags"];
const DISCOVERY_SPACE_MENU_ITEMS = [
    { key: "search", iconName: "sideBarSearch", label: "학술검색", path: "search" },
    { key: "report", iconName: "sideBarReport", label: "동향분석", path: "report" },
];
const WORK_SPACE_MENU_ITEMS = [
    { key: "reference", iconName: "sideBarReference", label: "참고문헌", path: "reference" },
    { key: "note", iconName: "sideBarNote", label: "연구노트", path: "note" },
    { key: "manuscript", iconName: "sideBarManuscript", label: "논문원고", path: "manuscript" },
];

const SpaceMenuContainer = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${Constants.SPACE_BAR_WIDTH}px;
    width: ${SIDE_WIDTH_BASE_PX};
    min-width: ${SIDE_WIDTH_MIN_PX};
    max-width: ${SIDE_WIDTH_MAX_PX};
    height: calc(100vh - 56px);
    background-color: var(--color-White);
    resize: none;
    transition: ${({ $isResizing }) => ($isResizing ? "none" : "all 350ms ease-in-out")};
    z-index: 6;

    ${(props) =>
        props.$device.isPC &&
        css`
            width: ${SIDE_WIDTH_BASE_PX};
            min-width: ${SIDE_WIDTH_MIN_PX};
            max-width: ${SIDE_WIDTH_MAX_PX};
            position: relative;
            transition: ${({ $isResizing }) => ($isResizing ? "none" : "all 350ms ease-in-out")};
        `};
`;

const SpaceMenuContents = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: all 180ms ease-in-out;
`;

const SpaceMenuContentsWrap = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: ${({ $isFold }) => ($isFold ? "8px 2px 10px 2px" : "8px 10px 10px 10px")};
`;

const SpaceMenuTopSlot = styled.div`
    height: 56px;
    margin: 0 6px;
    flex-shrink: 0;
`;

const SpaceMenuTitleSection = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
`;

const SpaceMenuTitle = styled(Text)`
    min-width: 0;
    font-size: 1.8rem;
    font-weight: 500;
    color: var(--color-Black);
    background-color: var(--color-TitleBar);
    padding: 0px 4px;
    border-radius: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-decoration: none;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid var(--color-Key);
        outline-offset: 2px;
    }
`;

const SpaceMenuTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
`;

const SpaceMenuHeaderActions = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 8px;
`;

const SpaceMenuHeaderActionButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 140ms ease-in;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const SpaceMenuTitleDivider = styled.div`
    height: 1px;
    margin: 0 6px;
    background-color: var(--color-Grey3);
`;

const SpaceMenuGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 14px;
`;

const SpaceMenuDivider = styled.div`
    height: 1px;
    margin: 14px 6px 0 6px;
    background-color: var(--color-Grey3);
    flex-shrink: 0;

    ${({ $isFold }) =>
        $isFold &&
        css`
            margin-left: 0;
            margin-right: 0;
        `};
`;

const SpaceMenuNavList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const SpaceMenuSection = styled.div`
    display: flex;
    align-items: center;
    margin: 0;
    width: 100%;
    min-width: ${({ $isFold }) => ($isFold ? "0" : "200px")};
    height: 44px;
    justify-content: ${({ $isFold }) => ($isFold ? "center" : "flex-start")};
`;

const SpaceMenuItem = styled(Link)`
    position: relative;
    display: flex;
    align-items: center;
    margin: 0;
    padding: ${({ $isFold }) => ($isFold ? "0" : "0 10px 0 16px")};
    width: ${({ $isFold }) => ($isFold ? "40px" : "calc(100% - 12px)")};
    height: 40px;
    background-color: ${(props) => (props.$isActive ? "var(--color-ButtonHover4)" : "transparent")};
    border: none;
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    transition: background-color 140ms ease-in;
    justify-content: ${({ $isFold }) => ($isFold ? "center" : "space-between")};

    &::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 8px;
        width: 3px;
        height: 16px;
        border-radius: 2px;
        background-color: ${(props) => (props.$isActive ? "var(--color-Key)" : "transparent")};
        transform: translateY(-50%);
        transition: background-color 140ms ease-in;
        opacity: ${({ $isFold }) => ($isFold ? "0" : "1")};
    }

    &:hover {
        background-color: ${(props) => (props.$isActive ? "var(--color-ButtonHover4)" : "rgba(0, 154, 126, 0.09)")};
    }
`;

const SpaceMenuItemLeftSide = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ $isFold }) => ($isFold ? "0" : "12px")};
    justify-content: ${({ $isFold }) => ($isFold ? "center" : "flex-start")};
    width: ${({ $isFold }) => ($isFold ? "100%" : "auto")};
    min-width: 0;
    overflow: hidden;
`;

const SpaceMenuTitleText = styled(Text)`
    min-width: 0;
    font-size: 1.6rem;
    font-weight: 500;
    color: var(--color-Black);
    overflow: hidden;
    white-space: nowrap;
    ${({ $isFold }) =>
        $isFold &&
        css`
            display: none;
        `};
`;

const SpaceMenuReferenceTabsPanel = styled.div`
    width: 100%;
    border-radius: 4px;
    background-color: transparent;
    overflow: hidden;
`;

const SpaceMenuReferenceContent = styled.div`
    max-height: ${({ $isFold }) => ($isFold ? "0" : "640px")};
    opacity: ${({ $isFold }) => ($isFold ? "0" : "1")};
    overflow: hidden;
    transition:
        max-height 220ms ease-in-out,
        opacity 140ms ease-in-out;
`;

const SpaceMenuTabContainer = styled.div`
    display: flex;
    align-items: flex-start;
    margin: 6px 6px 8px 16px;
    width: calc(100% - 28px);
    min-height: 120px;
    flex-direction: column;
    flex: 1;
`;

const SpaceMenuTabButtonWrap = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    margin: 0;
    padding: 6px 8px 0 8px;
    gap: 4px;
    border-bottom: 1px solid var(--color-Grey3);
`;

const SpaceMenuTabButton = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 32px;
    padding: 0 10px;
    border: 1px solid transparent;
    border-bottom: 0;
    border-radius: 4px 4px 0 0;
    background-color: ${(props) => (props.$isActive ? "var(--color-Base1)" : "transparent")};
    cursor: pointer;

    ${(props) =>
        props.$isActive &&
        css`
            border-color: var(--color-Grey3);
        `};
`;

const SpaceMenuTabButtonText = styled(Text)`
    font-size: 1.4rem;
    font-weight: ${({ $isActive }) => ($isActive ? "600" : "500")};
    color: ${({ $isActive }) => ($isActive ? "var(--color-Key)" : "var(--color-SubBlack)")};
`;

const SpaceMenuTabButtonBar = styled.span`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: block;
    height: 1px;
    border-radius: 2px;
    background-color: ${(props) => (props.$isActive ? "var(--color-White)" : "transparent")};
`;

const SpaceMenuTabMoreButtonWrap = styled.div`
    display: flex;
    margin-left: auto;
    gap: 4px;
`;

const SpaceMenuScrollContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    width: 100%;
    padding: 10px 8px 8px 8px;
    background-color: transparent;
`;

const SpaceMenuCustomContent = styled.div`
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: ${({ $isFold }) => ($isFold ? "0" : "8px 0 0 0")};
    opacity: ${({ $isFold }) => ($isFold ? "0" : "1")};
    pointer-events: ${({ $isFold }) => ($isFold ? "none" : "auto")};
`;

const FoldToggleSection = styled(SpaceMenuSection)`
    height: 56px;
`;

const NavigatorSection = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const NavigatorMenuRow = styled(SpaceMenuSection)``;

const SpaceMenuResizerWrap = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(100% - 6px);
    z-index: 10;
    display: flex;
    justify-content: center;
    width: 12px;
    resize: horizontal;
    user-select: none;

    ${(props) =>
        props.$device.isPC &&
        css`
            cursor: ${({ $isFold }) => ($isFold ? "default" : "ew-resize")};

            &:hover {
                cursor: ${({ $isFold }) => ($isFold ? "default !important" : "ew-resize !important")};
                .toggleButton {
                    background-color: var(--color-Key);
                }
            }
        `};
`;

const SpaceMenuResizerLine = styled.div`
    position: relative;
    width: 3px;
    height: 100%;
    pointer-events: none;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: -1px;
        width: 3px;
        background-color: var(--color-Grey3);
        opacity: 0.5;
        transition:
            left 0.22s cubic-bezier(0.2, 0, 0, 1),
            opacity 0.22s cubic-bezier(0.2, 0, 0, 1),
            width 0.22s cubic-bezier(0.2, 0, 0, 1);
    }

    &::after {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        right: 1px;
        width: 3px;
        background-color: ${({ $device, $isHovering, $isFold }) =>
            $device.isPC && !$isFold && $isHovering ? "var(--color-Key)" : "transparent"};
        opacity: ${({ $device, $isHovering, $isFold }) => ($device.isPC && !$isFold && $isHovering ? "1" : "0")};
        transition: all 200ms ease;
    }
`;

const VolumeSection = styled.div`
    margin: 0 6px 6px 6px;
    padding: 16px;
    border-radius: 4px;
    border: 1px solid rgba(17, 24, 39, 0.08);
    background: var(--color-White);
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
`;

const VolumeSectionWrap = styled.div`
    margin-top: auto;
    max-height: ${({ $isFold }) => ($isFold ? "0" : "240px")};
    opacity: ${({ $isFold }) => ($isFold ? "0" : "1")};
    transform: ${({ $isFold }) => ($isFold ? "translateY(8px)" : "none")};
    pointer-events: ${({ $isFold }) => ($isFold ? "none" : "auto")};
    overflow: ${({ $isFold }) => ($isFold ? "hidden" : "visible")};
    transition:
        max-height 220ms ease-in-out,
        opacity 160ms ease-in-out,
        transform 220ms ease-in-out;
`;

const VolumeHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
`;

const PlanTextWrap = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;

    > div {
        max-width: 100%;
    }
`;

const PlanText = styled(Text)`
    display: inline-block;
    max-width: 100%;
    min-width: 0;
    font-size: 1.4rem;
    font-weight: 500;
    color: var(--color-Black);
    margin-bottom: 8px;
    cursor: pointer;
    overflow: hidden;
    white-space: nowrap;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const VolumeText = styled(Text)`
    min-width: 0;
    font-size: 1.2rem;
    font-weight: 400;
    color: ${(props) => (props.$warn ? "var(--color-Warn)" : "var(--color-Black)")};
    overflow: hidden;
    white-space: nowrap;
`;

const BarWrap = styled.div`
    position: relative;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background-color: var(--color-Grey3);
    overflow: hidden;
`;

const Guage = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    height: 8px;
    background-color: ${(props) => (props.$warn ? "var(--color-Warn)" : "var(--color-Key)")};
    width: ${(props) => props.$guage || 0}%;
    transition: width 0.3s ease;
`;

const CapacityInfo = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
    font-size: 1.2rem;
    color: var(--color-Black);
    margin-top: 4px;
`;

const SpaceMenuTitleLabel = styled(Text)`
    min-width: 0;
    font-size: 1.1rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    margin-left: 4px;
    margin-bottom: 2px;
    overflow: hidden;
    white-space: nowrap;
`;

function NavigatorDetailsPanel({
    isFold,
    activeTab,
    handleTabClick,
    navigate,
    space_uuid,
    searchParams,
    showTagAddModal,
    setShowTagAddModal,
    showFolderAddModal,
    setShowFolderAddModal,
    user,
}) {
    const navigateNavigator = (nextState = {}) => {
        const params = new URLSearchParams();
        if (searchParams?.search) params.set("search", searchParams.search);
        if (searchParams?.favorites) params.set("favorites", "true");
        if (searchParams?.ordering) params.set("ordering", searchParams.ordering);
        if (searchParams?.page_size) params.set("page_size", searchParams.page_size);
        if (nextState.folder?.length > 0) params.set("folder", nextState.folder[0]);
        if (nextState.tags?.length > 0) params.set("tags", nextState.tags.join(","));
        navigate(`/${space_uuid}/navigator${params.toString() ? `?${params.toString()}` : ""}`);
    };

    return (
        <SpaceMenuReferenceContent $isFold={isFold}>
            <SpaceMenuTabContainer>
                <SpaceMenuReferenceTabsPanel>
                    <SpaceMenuTabButtonWrap>
                        {SIDEBAR_TAB.map((tab) => {
                            const isActive = activeTab === tab;

                            return (
                                <SpaceMenuTabButton key={tab} $isActive={isActive} onClick={() => handleTabClick(tab)}>
                                    <SpaceMenuTabButtonText $isActive={isActive}>{tab}</SpaceMenuTabButtonText>
                                    <SpaceMenuTabButtonBar $isActive={isActive} />
                                </SpaceMenuTabButton>
                            );
                        })}
                        <SpaceMenuTabMoreButtonWrap>
                            <Tooltip message={activeTab === "Tags" ? "태그 추가" : "폴더 추가"}>
                                <Button
                                    onlyIcon
                                    bgColor={"transparent"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    onClick={() => {
                                        if (activeTab === "Tags") {
                                            setShowTagAddModal(true);
                                            return;
                                        }

                                        setShowFolderAddModal({
                                            type: "create",
                                            parentIndex: searchParams?.folder ? searchParams?.folder : null,
                                        });
                                    }}
                                    disabled={isReadonlyUser(user)}
                                >
                                    <Icon name={"plus"} size={"12"} color={"var(--color-DisabledText)"} />
                                </Button>
                            </Tooltip>
                        </SpaceMenuTabMoreButtonWrap>
                    </SpaceMenuTabButtonWrap>
                    <SpaceMenuScrollContainer>
                        {activeTab === "Tags" ? (
                            <NavigatorTagTreeView
                                treeId={Constants.TAG_TREE_ID_SIDEMENU}
                                onSelectItems={(items) => {
                                    navigateNavigator({ tags: items || [] });
                                }}
                                searchParams={searchParams}
                                showTagAddModal={showTagAddModal}
                                setShowTagAddModal={setShowTagAddModal}
                                allLabel={"모든 연구자료"}
                            />
                        ) : (
                            <NavigatorFolderTreeView
                                treeId={"folder-view"}
                                searchParams={searchParams}
                                onSelectItems={(items) => {
                                    navigateNavigator({ folder: items || [] });
                                }}
                                showFolderAddModal={showFolderAddModal}
                                setShowFolderAddModal={setShowFolderAddModal}
                                allLabel={"모든 연구자료"}
                            />
                        )}
                    </SpaceMenuScrollContainer>
                </SpaceMenuReferenceTabsPanel>
            </SpaceMenuTabContainer>
        </SpaceMenuReferenceContent>
    );
}

function SpaceMenuBar({
    device,
    wBarRef,
    isFold,
    wBarSize,
    wBarResize,
    wBarHover,
    startWBarResizing,
    setWBarHover,
    toggleSidebar,
    spaces,
    space_uuid,
    navigate,
    isPathReference,
    isPathNavigator,
    activeTab,
    handleTabClick,
    searchParams,
    showTagAddModal,
    setShowTagAddModal,
    showFolderAddModal,
    setShowFolderAddModal,
    user,
    setShowPaidPlanSubscribeModal,
    volume,
    isVolumeWarn,
    formatBytes,
    primaryMenuActiveMap,
    customMode = false,
    customTitleLabel = "",
    customTitle = "",
    customTitleAction = null,
    customContent = null,
    showCustomVolumeSection = false,
}) {
    const currentSpaceName = spaces?.find((space) => space.uuid === space_uuid)?.name || "";

    const renderSpaceMenuItem = ({ to, isActive, iconName, label, state }) => {
        const menuItem = (
            <SpaceMenuItem $isActive={isActive} $isFold={isFold} to={to} state={state}>
                <SpaceMenuItemLeftSide $isFold={isFold}>
                    <Icon name={iconName} size={"16px"} color={isActive ? "var(--color-Key)" : undefined} />
                    <SpaceMenuTitleText $isActive={isActive} $isFold={isFold}>
                        {label}
                    </SpaceMenuTitleText>
                </SpaceMenuItemLeftSide>
            </SpaceMenuItem>
        );

        if (isFold) {
            return <Tooltip message={label}>{menuItem}</Tooltip>;
        }

        return menuItem;
    };

    const renderFoldToggleButton = () => {
        const toggleButton = (
            <SpaceMenuItem as="button" type="button" $isActive={false} $isFold={true} onClick={toggleSidebar}>
                <SpaceMenuItemLeftSide $isFold={true}>
                    <Icon name={"sidemenuOpen"} size={"16px"} color={"var(--color-Button2)"} />
                </SpaceMenuItemLeftSide>
            </SpaceMenuItem>
        );

        return <Tooltip message={"열기"}>{toggleButton}</Tooltip>;
    };

    const renderVolumeSection = () => (
        <VolumeSectionWrap $isFold={isFold}>
            <VolumeSection>
                <VolumeHeader>
                    <PlanTextWrap>
                        <Tooltip message={"요금제 확인"}>
                            <PlanText onClick={() => navigate(`/${space_uuid}/management`)}>
                                {user?.current_space?.plan?.name} 요금제
                            </PlanText>
                        </Tooltip>
                    </PlanTextWrap>
                    {isFreePlanUser(user) && (
                        <Button
                            onlyText
                            width={"72px"}
                            height={"24px"}
                            bgColor={"var(--color-Button1)"}
                            hoverBgColor={"var(--color-ButtonHover1)"}
                            fontColor={"var(--color-White)"}
                            buttonText={"업그레이드"}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setShowPaidPlanSubscribeModal(true);
                            }}
                            singleButton
                        />
                    )}
                </VolumeHeader>
                <BarWrap>
                    <Guage $guage={(volume?.used / volume?.max) * 100} $warn={isVolumeWarn} />
                </BarWrap>
                <CapacityInfo>
                    <VolumeText $warn={isVolumeWarn}>
                        {formatBytes(volume?.used)} / {formatBytes(volume?.max)}
                    </VolumeText>
                    <VolumeText $warn={isVolumeWarn}>
                        {((volume?.used / volume?.max) * 100).toFixed(0)}% 사용중
                    </VolumeText>
                </CapacityInfo>
            </VolumeSection>
        </VolumeSectionWrap>
    );

    return (
        <SpaceMenuContainer
            $device={device}
            ref={wBarRef}
            $isFold={isFold}
            $isResizing={wBarResize}
            style={{ width: `${wBarSize}px` }}
        >
            <SpaceMenuContents>
                <SpaceMenuContentsWrap $isFold={isFold}>
                    {customMode ? (
                        <>
                            <SpaceMenuTopSlot>
                                {isFold ? (
                                    <FoldToggleSection $isFold={isFold}>{renderFoldToggleButton()}</FoldToggleSection>
                                ) : (
                                    <SpaceMenuTitleSection>
                                        {customTitleLabel && (
                                            <SpaceMenuTitleLabel>{customTitleLabel}</SpaceMenuTitleLabel>
                                        )}
                                        <SpaceMenuTitleRow>
                                            <SpaceMenuTitle>{customTitle}</SpaceMenuTitle>
                                            <SpaceMenuHeaderActions>
                                                {customTitleAction}
                                                <SpaceMenuHeaderActionButton type="button" onClick={toggleSidebar}>
                                                    <Tooltip message={"닫기"}>
                                                        <Icon
                                                            name={"sidemenuClose"}
                                                            size={"16px"}
                                                            color={"var(--color-Grey1)"}
                                                        />
                                                    </Tooltip>
                                                </SpaceMenuHeaderActionButton>
                                            </SpaceMenuHeaderActions>
                                        </SpaceMenuTitleRow>
                                    </SpaceMenuTitleSection>
                                )}
                            </SpaceMenuTopSlot>
                            <SpaceMenuTitleDivider />
                            <SpaceMenuCustomContent $isFold={isFold}>{customContent}</SpaceMenuCustomContent>
                            {showCustomVolumeSection && renderVolumeSection()}
                        </>
                    ) : (
                        <>
                            <SpaceMenuTopSlot>
                                {isFold ? (
                                    <FoldToggleSection $isFold={isFold}>{renderFoldToggleButton()}</FoldToggleSection>
                                ) : (
                                    <SpaceMenuTitleSection>
                                        <SpaceMenuTitleLabel>스페이스</SpaceMenuTitleLabel>
                                        <SpaceMenuTitleRow>
                                            <SpaceMenuTitle as={Link} to={`/${space_uuid}`}>
                                                {currentSpaceName}
                                            </SpaceMenuTitle>
                                            <SpaceMenuHeaderActions>
                                                <SpaceMenuHeaderActionButton
                                                    type="button"
                                                    onClick={() => navigate(`/${space_uuid}/management`)}
                                                >
                                                    <Tooltip message={"관리"}>
                                                        <Icon
                                                            name={"setting"}
                                                            size={"16px"}
                                                            color={"var(--color-Grey1)"}
                                                        />
                                                    </Tooltip>
                                                </SpaceMenuHeaderActionButton>
                                                <SpaceMenuHeaderActionButton type="button" onClick={toggleSidebar}>
                                                    <Tooltip message={"닫기"}>
                                                        <Icon
                                                            name={"sidemenuClose"}
                                                            size={"16px"}
                                                            color={"var(--color-Grey1)"}
                                                        />
                                                    </Tooltip>
                                                </SpaceMenuHeaderActionButton>
                                            </SpaceMenuHeaderActions>
                                        </SpaceMenuTitleRow>
                                    </SpaceMenuTitleSection>
                                )}
                            </SpaceMenuTopSlot>
                            <SpaceMenuTitleDivider />
                            <SpaceMenuGroup>
                                <SpaceMenuNavList>
                                    {DISCOVERY_SPACE_MENU_ITEMS.map((menuItem) => (
                                        <SpaceMenuSection key={menuItem.key} $isFold={isFold}>
                                            {renderSpaceMenuItem({
                                                to: `/${space_uuid}/${menuItem.path}`,
                                                isActive: primaryMenuActiveMap[menuItem.key],
                                                iconName: menuItem.iconName,
                                                label: menuItem.label,
                                            })}
                                        </SpaceMenuSection>
                                    ))}
                                </SpaceMenuNavList>
                            </SpaceMenuGroup>
                            <SpaceMenuDivider $isFold={isFold} />
                            <SpaceMenuGroup>
                                <SpaceMenuNavList>
                                    {WORK_SPACE_MENU_ITEMS.map((menuItem) => (
                                        <SpaceMenuSection key={menuItem.key} $isFold={isFold}>
                                            {renderSpaceMenuItem({
                                                to: `/${space_uuid}/${menuItem.path}`,
                                                isActive: primaryMenuActiveMap[menuItem.key],
                                                iconName: menuItem.iconName,
                                                label: menuItem.label,
                                            })}
                                        </SpaceMenuSection>
                                    ))}
                                </SpaceMenuNavList>
                            </SpaceMenuGroup>
                            <SpaceMenuDivider $isFold={isFold} />
                            <SpaceMenuGroup>
                                <NavigatorSection>
                                    <NavigatorMenuRow $isFold={isFold}>
                                        {renderSpaceMenuItem({
                                            to: `/${space_uuid}/navigator`,
                                            isActive: isPathNavigator,
                                            iconName: "sideBarNavigator",
                                            label: "분류별 보기",
                                        })}
                                    </NavigatorMenuRow>
                                    <NavigatorDetailsPanel
                                        isFold={isFold}
                                        activeTab={activeTab}
                                        handleTabClick={handleTabClick}
                                        navigate={navigate}
                                        space_uuid={space_uuid}
                                        searchParams={searchParams}
                                        showTagAddModal={showTagAddModal}
                                        setShowTagAddModal={setShowTagAddModal}
                                        showFolderAddModal={showFolderAddModal}
                                        setShowFolderAddModal={setShowFolderAddModal}
                                        user={user}
                                    />
                                </NavigatorSection>
                            </SpaceMenuGroup>
                            {renderVolumeSection()}
                        </>
                    )}
                </SpaceMenuContentsWrap>
            </SpaceMenuContents>
            <SpaceMenuResizerWrap
                $device={device}
                $isFold={isFold}
                $isHovering={wBarHover}
                onMouseDown={startWBarResizing}
                onMouseEnter={() => setWBarHover(true)}
                onMouseLeave={() => setWBarHover(false)}
            >
                <SpaceMenuResizerLine $device={device} $isHovering={wBarHover} $isFold={isFold} />
            </SpaceMenuResizerWrap>
        </SpaceMenuContainer>
    );
}

export default SpaceMenuBar;
